import { ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { UnsubscribeOnDestroyAdapter } from '../../../../adapters/unsubscibe-on-destroy.adapter';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { CalculatorInputManager } from '../../common/calculator-input-manager';
import { AnalysisService } from '../../../../../services/analysis.service';
import { LoaderService } from '../../../../../services/loader.service';
import { ToastService } from '../../../../../services/toast.service';
import { AuthService } from '../../../../../services/auth/auth.service';
import { RoofSegmentSizeAndSunshineStats } from '../../../../models/maps/building-insights';
import { inOutAnimation } from '../../../../animations';
import { UserRole } from 'src/common/models/user/user-role.enum';
import { roofAzimuthConst, roofTiltValuesConst } from '../../common/calculator-constants';
import { SimpleSolarCalculatorRequest } from '../../../../models/analysis/simple-solar-calculator-request';
import { SimpleSolarCalculatorResult } from '../../../../models/analysis/simple-solar-calculator-result';
import { finalize } from 'rxjs';
import { CalculatorColorConfig, defaultCalculatorColorConfig } from './calculator-color-config';

@Component({
  selector: 'psc-simple-calculator',
  templateUrl: './simple-calculator.component.html',
  animations: [
    inOutAnimation({ enterDelay: 0.5 }),
    inOutAnimation({ name: 'inOutFromRight', translationDirection: 'X' }),
  ],
})
export class SimpleCalculatorComponent extends UnsubscribeOnDestroyAdapter implements OnInit {
  @Input() colorConfig: CalculatorColorConfig = defaultCalculatorColorConfig;
  @Output() resultCalculated: EventEmitter<SimpleSolarCalculatorResult> = new EventEmitter<SimpleSolarCalculatorResult>();

  calculatorState: SimpleCalculatorState = SimpleCalculatorState.SelectLocation;

  formGroup: FormGroup;

  manager: CalculatorInputManager = new CalculatorInputManager();

  protected readonly roofAzimuthValues = roofAzimuthConst;
  protected readonly roofTiltValues = roofTiltValuesConst;

  protected readonly UserRole = UserRole;

  constructor(
    private formBuilder: FormBuilder,
    private analysisService: AnalysisService,
    private loadingService: LoaderService,
    private toastService: ToastService,
    private authService: AuthService,
    private changeDetectorRef: ChangeDetectorRef,
  ) {
    super();
  }

  ngOnInit(): void {
    this.formGroup = this.formBuilder.group({
      tilt: ['', Validators.required],
      azimuth: ['', Validators.required],
      area: ['', Validators.required],
      consumption_average_bill: ['', Validators.required],
      googleLatLng: ['', Validators.required],
      user_contact: this.authService.getUserEmail() || '',
    });
  }

  setLocation(location: google.maps.LatLngLiteral) {
    this.formGroup.controls['googleLatLng'].setValue(location);
    this.manager.location.next(location);
  }

  onSubmit() {
    const formValue = this.formGroup.value;

    const request: SimpleSolarCalculatorRequest = {
      area: formValue.area,
      azimuth: formValue.azimuth,
      tilt: formValue.tilt,
      consumptionAverageBill: formValue.consumption_average_bill,
      userContact: formValue.user_contact,
      googleLatLng: formValue.googleLatLng,
    };

    this.loadingService.showLoader();
    this.analysisService.getBasicBankAnalysis(request)
      .pipe(finalize(() => {
        this.loadingService.hideLoader();
      }))
      .subscribe(
      (resultWrapper) => {
        this.resultCalculated.emit(resultWrapper);
        this.calculatorState = SimpleCalculatorState.Submitted;
      });
  }

  roofSegmentSelected(roofSegment: RoofSegmentSizeAndSunshineStats) {
    this.formGroup.controls['area'].setValue(roofSegment.stats.areaMeters2.toFixed());
    this.formGroup.controls['azimuth'].setValue(roofSegment.azimuthDegrees.toFixed());
    this.formGroup.controls['tilt'].setValue(roofSegment.pitchDegrees.toFixed());
    this.formGroup.updateValueAndValidity();
    this.changeDetectorRef.detectChanges();
    this.calculatorState = SimpleCalculatorState.InputLocationData;
  }

  protected readonly SimpleCalculatorState = SimpleCalculatorState;
}

export enum SimpleCalculatorState {
  SelectLocation,
  InputLocationData,
  ConsumptionInfo,
  Submitted,
}
